import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { AuthCard } from '@/components/portal/auth-card'
import { Wordmark } from '@/components/portal/wordmark'
import { forgotPassword, verifyPasswordOtp } from '@/lib/auth'
import { cn } from '@/lib/utils'

type Mode = 'email' | 'phone'
type PhoneStep = 'request' | 'acknowledge' | 'verify'

type SmsDelivery = 'sent' | 'failed' | 'skipped' | 'not_sent' | 'logged_only'

interface PhoneAcknowledge {
  phone: string
  smsDelivery?: SmsDelivery
  smsError?: string | null
  devOtp?: string | null
  atSandbox?: boolean
}

function maskPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '')
  if (digits.length < 4) return phone
  return `${phone.slice(0, 4)}···${phone.slice(-3)}`
}

function PhoneAcknowledgeCard({ ack }: { ack: PhoneAcknowledge }) {
  const { smsDelivery, smsError, devOtp, phone, atSandbox } = ack

  const sandboxNote = atSandbox ? (
    <p className="mt-2 text-xs leading-relaxed opacity-90">
      Sandbox mode — SMS only reaches numbers added under{' '}
      <strong>Africa&apos;s Talking → Sandbox → Phone numbers</strong>. Use your sandbox API key and{' '}
      <code className="text-[11px]">AT_USERNAME=sandbox</code>.
    </p>
  ) : null

  if (smsDelivery === 'sent') {
    return (
      <div
        className="rounded border border-status-paid-bg bg-status-paid-bg/40 px-4 py-3 font-sans text-sm text-status-paid-text"
        role="status"
      >
        <p className="font-medium">{atSandbox ? 'Sandbox SMS sent' : 'SMS sent'}</p>
        <p className="mt-1 text-xs leading-relaxed">
          A 6-digit code was sent to <strong>{maskPhone(phone)}</strong>. It expires in 10 minutes.
        </p>
        {sandboxNote}
      </div>
    )
  }

  if (smsDelivery === 'failed') {
    return (
      <div className="space-y-3">
        <div
          className="rounded border border-destructive/30 bg-destructive/10 px-4 py-3 font-sans text-sm text-destructive"
          role="alert"
        >
          <p className="font-medium">SMS could not be delivered</p>
          {smsError && <p className="mt-1 text-xs leading-relaxed">{smsError}</p>}
          {sandboxNote}
        </div>
        {devOtp && (
          <div className="rounded border border-status-pending-bg bg-status-pending-bg/40 px-4 py-3 font-sans text-xs text-status-pending-text">
            Use this code to continue testing: <strong className="text-sm">{devOtp}</strong>
          </div>
        )}
      </div>
    )
  }

  if (smsDelivery === 'skipped') {
    return (
      <div className="space-y-3">
        <div
          className="rounded border border-status-pending-bg bg-status-pending-bg/40 px-4 py-3 font-sans text-sm text-status-pending-text"
          role="status"
        >
          <p className="font-medium">SMS blocked (development safety)</p>
          <p className="mt-1 text-xs leading-relaxed">
            No message was sent to {maskPhone(phone)}. Set{' '}
            <code className="text-[11px]">SMS_STAGING_REDIRECT_TO</code> or{' '}
            <code className="text-[11px]">SMS_STAGING_ALLOWLIST</code> in{' '}
            <code className="text-[11px]">.dev.vars</code>.
          </p>
        </div>
        {devOtp && (
          <div className="rounded border border-status-pending-bg bg-status-pending-bg/40 px-4 py-3 font-sans text-xs text-status-pending-text">
            Use this code to continue testing: <strong className="text-sm">{devOtp}</strong>
          </div>
        )}
      </div>
    )
  }

  if (smsDelivery === 'logged_only') {
    return (
      <div className="space-y-3">
        <div
          className="rounded border border-status-pending-bg bg-status-pending-bg/40 px-4 py-3 font-sans text-sm text-status-pending-text"
          role="status"
        >
          <p className="font-medium">SMS not configured (local dev)</p>
          <p className="mt-1 text-xs leading-relaxed">
            No message was sent to the network. Check the API terminal for the would-send log.
          </p>
        </div>
        {devOtp && (
          <div className="rounded border border-status-pending-bg bg-status-pending-bg/40 px-4 py-3 font-sans text-xs text-status-pending-text">
            Use this code to continue testing: <strong className="text-sm">{devOtp}</strong>
          </div>
        )}
      </div>
    )
  }

  if (smsDelivery === 'not_sent') {
    return (
      <div
        className="rounded border border-status-pending-bg bg-status-pending-bg/40 px-4 py-3 font-sans text-sm text-status-pending-text"
        role="status"
      >
        <p className="font-medium">No SMS was sent</p>
        <p className="mt-1 text-xs leading-relaxed">
          This number may not match an active account, or the request could not be processed. Check
          the phone format (<strong>+254XXXXXXXXX</strong>) and try a registered member number.
        </p>
      </div>
    )
  }

  return (
    <div
      className="rounded border border-status-paid-bg bg-status-paid-bg/40 px-4 py-3 font-sans text-sm text-status-paid-text"
      role="status"
    >
      <p className="font-medium">Request received</p>
      <p className="mt-1 text-xs leading-relaxed">
        If an account exists for <strong>{maskPhone(phone)}</strong>, you will receive a 6-digit
        code shortly. Check your messages, then enter the code on the next step.
      </p>
    </div>
  )
}

export function ForgotPasswordPage() {
  const navigate = useNavigate()
  const [mode, setMode] = useState<Mode>('email')
  const [phoneStep, setPhoneStep] = useState<PhoneStep>('request')
  const [phoneAck, setPhoneAck] = useState<PhoneAcknowledge | null>(null)
  const [identifier, setIdentifier] = useState('')
  const [otp, setOtp] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [emailSubmitted, setEmailSubmitted] = useState(false)
  const [devResetUrl, setDevResetUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  function switchMode(next: Mode) {
    setMode(next)
    setError(null)
    setDevResetUrl(null)
    setEmailSubmitted(false)
    setPhoneStep('request')
    setPhoneAck(null)
    setIdentifier('')
    setOtp('')
  }

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)

    try {
      const result = await forgotPassword(identifier)
      if (result.resetUrl) {
        setDevResetUrl(result.resetUrl)
      }
      setEmailSubmitted(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handlePhoneRequest(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)

    try {
      const result = await forgotPassword(identifier)
      setPhoneAck({
        phone: identifier.trim(),
        smsDelivery: result.smsDelivery,
        smsError: result.smsError ?? null,
        devOtp: result.devOtp ?? null,
        atSandbox: result.atSandbox,
      })
      setPhoneStep('acknowledge')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handlePhoneVerify(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)

    try {
      const result = await verifyPasswordOtp(identifier, otp)
      navigate(`/reset-password?token=${encodeURIComponent(result.resetToken)}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid or expired code.')
    } finally {
      setSubmitting(false)
    }
  }

  const phoneSubtitle =
    phoneStep === 'request'
      ? 'Enter the phone number on your account. We will send a reset code.'
      : phoneStep === 'acknowledge'
        ? 'Review the status below, then continue to enter your code.'
        : 'Enter the 6-digit code we sent to your phone.'

  return (
    <AuthCard>
      <Wordmark portal="Estate Portal" className="mb-8" />

      <h2 className="mb-2 text-center font-serif text-2xl font-medium text-ink">
        Reset your password
      </h2>
      <p className="mb-6 text-center font-sans text-sm text-muted-foreground">
        {mode === 'email'
          ? 'Enter the email on your account. We will send a reset link.'
          : phoneSubtitle}
      </p>

      <div className="mb-6 flex rounded border border-border-2 p-1">
        <button
          type="button"
          className={cn(
            'flex-1 rounded px-3 py-2 font-sans text-sm transition-colors',
            mode === 'email' ? 'bg-green-pale text-ink' : 'text-muted-foreground hover:text-ink',
          )}
          onClick={() => switchMode('email')}
        >
          Email
        </button>
        <button
          type="button"
          className={cn(
            'flex-1 rounded px-3 py-2 font-sans text-sm transition-colors',
            mode === 'phone' ? 'bg-green-pale text-ink' : 'text-muted-foreground hover:text-ink',
          )}
          onClick={() => switchMode('phone')}
        >
          Phone
        </button>
      </div>

      {mode === 'email' && emailSubmitted ? (
        <div className="space-y-4 text-center">
          <p className="rounded border border-status-paid-bg bg-status-paid-bg/40 px-3 py-3 font-sans text-sm text-status-paid-text">
            If an account exists for this email, you will receive a reset link shortly.
          </p>
          {devResetUrl && (
            <p className="rounded border border-status-pending-bg bg-status-pending-bg/40 px-3 py-2 font-sans text-xs text-status-pending-text">
              Dev only (email not configured):{' '}
              <a href={devResetUrl} className="break-all text-primary hover:text-green-lt">
                {devResetUrl}
              </a>
            </p>
          )}
          <Link to="/login" className="font-sans text-sm text-primary hover:text-green-lt">
            Back to sign in
          </Link>
        </div>
      ) : mode === 'email' ? (
        <form onSubmit={handleEmailSubmit} className="space-y-5">
          <Input
            label="Email address"
            type="email"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            autoComplete="email"
            required
          />

          {error && (
            <p className="font-sans text-sm text-destructive" role="alert">
              {error}
            </p>
          )}

          <Button type="submit" className="w-full" size="lg" loading={submitting}>
            Send reset link
          </Button>
        </form>
      ) : phoneStep === 'acknowledge' && phoneAck ? (
        <div className="space-y-5">
          <PhoneAcknowledgeCard ack={phoneAck} />

          <Button
            type="button"
            className="w-full"
            size="lg"
            onClick={() => {
              setPhoneStep('verify')
              setOtp('')
              setError(null)
            }}
          >
            Enter reset code
          </Button>

          <button
            type="button"
            className="w-full font-sans text-sm text-muted-foreground hover:text-ink"
            onClick={() => {
              setPhoneStep('request')
              setPhoneAck(null)
              setError(null)
            }}
          >
            Use a different number
          </button>
        </div>
      ) : phoneStep === 'verify' ? (
        <form onSubmit={handlePhoneVerify} className="space-y-5">
          {phoneAck && <PhoneAcknowledgeCard ack={phoneAck} />}

          <Input
            label="Reset code"
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            autoComplete="one-time-code"
            required
          />

          {error && (
            <p className="font-sans text-sm text-destructive" role="alert">
              {error}
            </p>
          )}

          <Button type="submit" className="w-full" size="lg" loading={submitting}>
            Verify code
          </Button>

          <button
            type="button"
            className="w-full font-sans text-sm text-muted-foreground hover:text-ink"
            onClick={() => {
              setPhoneStep('request')
              setPhoneAck(null)
              setOtp('')
              setError(null)
            }}
          >
            Request a new code
          </button>
        </form>
      ) : (
        <form onSubmit={handlePhoneRequest} className="space-y-5">
          <Input
            label="Phone number"
            type="tel"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            autoComplete="tel"
            hint="Format: +254XXXXXXXXX"
            required
          />

          {error && (
            <p className="font-sans text-sm text-destructive" role="alert">
              {error}
            </p>
          )}

          <Button type="submit" className="w-full" size="lg" loading={submitting}>
            Send reset code
          </Button>
        </form>
      )}

      {!(mode === 'email' && emailSubmitted) && (
        <p className="mt-6 text-center font-sans text-sm">
          <Link to="/login" className="text-muted-foreground transition-colors hover:text-ink">
            Back to sign in
          </Link>
        </p>
      )}
    </AuthCard>
  )
}
